import { FC } from 'react';
import { AudioLines, Building2, CalendarClock, Plug } from 'lucide-react';

import { GmailIcon } from '@/icons/gmail-icon';
import { GoogleDriveIcon } from '@/icons/service-icons';
import { PersonalConnection } from '@/services/personal-connections/types';

// What the panel draws for a provider; everything else (name, description,
// status, actions, setup help) comes from the backend's provider record, so
// a provider added there appears here with a generic icon.
const ICONS: Record<string, FC<{ className?: string; size?: number }>> = {
  gmail: GmailIcon,
  googledrive: GoogleDriveIcon,
  attio: Building2,
  calendly: CalendarClock,
  sentra: AudioLines,
};

const CATEGORY: Record<string, string> = {
  google: 'Google Workspace',
  work: 'Work tools',
};

export const iconFor = (provider: string) => ICONS[provider] ?? Plug;

export const categoryOf = (connection: PersonalConnection) =>
  CATEGORY[connection.group] ?? 'Other';

export const FILTER_MIN = 6;
export const ALL_CATEGORIES = 'All';

const UNLINKED_STATUSES = new Set(['not_connected', 'pending', 'unavailable']);

export const isLinked = (connection: PersonalConnection) =>
  !UNLINKED_STATUSES.has(connection.status);

export const needsReconnect = (connection: PersonalConnection) =>
  isLinked(connection) && connection.status !== 'connected';

/**
 * Connected (live or broken grants) vs Available, plus the provider a
 * `?connect=` link asked for, which is pulled out so the page can show it
 * first with its one Connect button (the Active Agent deep-link contract).
 */
export const groupConnectors = (
  connections: PersonalConnection[] | undefined,
  focus?: string | null,
) => {
  const all = connections ?? [];
  const focused = focus
    ? (all.find((c) => c.provider === focus.toLowerCase()) ?? null)
    : null;
  const rest = all.filter((c) => c !== focused);
  return {
    focused,
    linked: rest.filter(isLinked),
    available: rest.filter((c) => !isLinked(c)),
  };
};

export const availableCategories = (available: PersonalConnection[]) => [
  ALL_CATEGORIES,
  ...new Set(available.map(categoryOf)),
];

export const filterAvailable = (
  available: PersonalConnection[],
  category: string,
  query: string,
) => {
  const q = query.trim().toLowerCase();
  return available.filter(
    (c) =>
      (category === ALL_CATEGORIES || categoryOf(c) === category) &&
      (!q || c.name.toLowerCase().includes(q)),
  );
};
